import React, { useEffect, useState } from "react";
import {
  ThinTextField,
  ThinDropdownWithSearch,
} from "../common/GeneralComponent";
import { fetchData } from "../../utils/functions";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";

const mealOptions = [
  { value: "One meal in a day", label: "One meal in a day" },
  { value: "Two meals in a day", label: "Two meals in a day" },
  { value: "Three meals in a day", label: "Three meals in a day" },
];

const Expenditure = ({ householdExpenditure = {}, DqaApproved, DqaRejected }) => {
  const [form, setForm] = useState({
    meals_per_day: householdExpenditure?.meals_per_day || "",
    tea_cups_per_day: householdExpenditure?.tea_cups_per_day ?? "",
    monthly_food_expenditure:
      householdExpenditure?.monthly_food_expenditure ?? "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDqaApproved, setIsDqaApproved] = useState(DqaApproved || false);
  const [isDqaRejected, setIsDqaRejected] = useState(DqaRejected || false);

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    setIsDqaApproved(DqaApproved || false);
    setIsDqaRejected(DqaRejected || false);
  }, [DqaApproved, DqaRejected]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Keep only digits for numeric fields
  const onlyDigits = (v) => v.replace(/[^\d]/g, "");

  const validate = () => {
    const e = {};
    const req = (v) => v !== null && v !== undefined && String(v).trim() !== "";

    if (!req(form.meals_per_day))
      e.meals_per_day = "Please select meals per day.";

    if (!req(form.tea_cups_per_day)) {
      e.tea_cups_per_day = "Cups per day is Required.";
    } else if (
      isNaN(Number(form.tea_cups_per_day)) ||
      Number(form.tea_cups_per_day) < 0
    ) {
      e.tea_cups_per_day = "Enter a non-negative number.";
    }

    if (!req(form.monthly_food_expenditure)) {
      e.monthly_food_expenditure = " Average monthly expenditure is Required.";
    } else if (
      isNaN(Number(form.monthly_food_expenditure)) ||
      Number(form.monthly_food_expenditure) < 0
    ) {
      e.monthly_food_expenditure = "Enter a non-negative number.";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!id) {
      toast.error("Household ID is missing");
      return;
    }
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        houshold_information_id: id,
        household_food_expenditure_id:
          householdExpenditure.household_food_expenditure_id,
        ...form,
      };

      const response = await fetchData(
        `household-food-expenditure-update/${householdExpenditure.household_food_expenditure_id}`,
        "PUT",
        payload,
        {},
        {},
        navigate,
        "/household",
        dispatch
      );

      if (response?.success) {
        toast.success("Household expenditure updated successfully!");
      } else {
        toast.error(response?.message || "Failed to update expenditure");
      }
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update expenditure.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white">
      {/* <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {isDqaApproved && (
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          )}
        </div>
      </div> */}
      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Food Expenditure
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Food Expenditure
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Meals per day */}
        <div>
          <ThinDropdownWithSearch
            label="How many meals do you have in a day?"
            options={mealOptions}
            value={form.meals_per_day}
            onChange={(value) => setField("meals_per_day", value)}
            required
            disabled={isDqaApproved || isDqaRejected}
          />
          {errors.meals_per_day && (
            <p className="text-[0.9rem] text-red-600 mt-2">
              {errors.meals_per_day}
            </p>
          )}
        </div>

        {/* Tea cups per day */}
        <div>
          <ThinTextField
            label="How many cups of tea do you all have in a day in hotels?"
            type="number"
            value={form.tea_cups_per_day}
            onChange={(e) =>
              setField("tea_cups_per_day", onlyDigits(e.target.value))
            }
            disabled={isDqaApproved || isDqaRejected}
            required
          />
          {errors.tea_cups_per_day && (
            <p className="text-[0.9rem] text-red-600 mt-2">
              {errors.tea_cups_per_day}
            </p>
          )}
        </div>

        {/* Monthly food expenditure */}
        <div className="md:col-span-2 md:max-w-md">
          <ThinTextField
            label="What is the household's average monthly expenditure on food?"
            type="number"
            value={form.monthly_food_expenditure}
            onChange={(e) =>
              setField(
                "monthly_food_expenditure",
                onlyDigits(e.target.value.slice(0, 5)),
              )
            }
            disabled={isDqaApproved || isDqaRejected}
            required
          />
          {errors.monthly_food_expenditure && (
            <p className="text-[0.9rem] text-red-600 mt-2">
              {errors.monthly_food_expenditure}
            </p>
          )}
        </div>
      </div>

      {/* Update Button - Only show when not DQA approved */}
      {!isDqaApproved && !isDqaRejected && (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className={`${
              loading ? "bg-gray-400" : "bg-teal-600 hover:bg-teal-700"
            } bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors`}
          >
            {loading ? "Updating..." : "Update"}
          </button>
        </div>
      )}
    </div>
  );
};

export default Expenditure;
